import React from 'react';
import { Crown, Sparkle } from '@phosphor-icons/react';

/**
 * <UserBadge /> — visual marker for founder / founding-member status.
 *
 *   <UserBadge type="founder" />           // gold crown, full label
 *   <UserBadge type="founder" size="sm" /> // crown only, compact
 *   <UserBadge type="founding_member" rank={7} />
 *
 * Pass an array of badge objects to <UserBadgeList /> to render the highest-
 * tier badge a user owns (founder > founding_member). Two badges are never
 * rendered together — founders skip the founding_member tier by design.
 */

const TIER = { founder: 2, founding_member: 1 };

function pickPrimary(badges) {
  if (!Array.isArray(badges) || badges.length === 0) return null;
  return badges.slice().sort((a, b) => (TIER[b.badge_type] || 0) - (TIER[a.badge_type] || 0))[0];
}

export function UserBadge({ type, rank, size = 'md', showLabel = true, className = '' }) {
  if (!type) return null;
  const isFounder = type === 'founder';
  const Icon = isFounder ? Crown : Sparkle;

  const sz = size === 'sm' ? 10 : size === 'lg' ? 16 : 12;
  const padding = size === 'sm' ? 'px-1.5 py-0.5' : size === 'lg' ? 'px-2.5 py-1' : 'px-2 py-0.5';
  const textSize = size === 'sm' ? 'text-[9px]' : size === 'lg' ? 'text-xs' : 'text-[10px]';

  // Founder = warm gold; Founding-member = cool teal/violet
  const colorClasses = isFounder
    ? 'bg-gradient-to-r from-[#FFB347]/20 to-[#FFD700]/15 border-[#FFB347]/50 text-[#FFD700]'
    : 'bg-gradient-to-r from-[#9D4CDD]/15 to-[#00F0FF]/10 border-[#9D4CDD]/40 text-[#9D4CDD]';

  const label = isFounder ? 'FOUNDER' : `FOUNDING #${rank || ''}`.trim();

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border ${padding} ${colorClasses} font-bold tracking-wider ${textSize} shrink-0 ${className}`}
      data-testid={`user-badge-${type}`}
      title={isFounder ? 'CyBoard Founder' : `Founding Member #${rank || ''}`}
    >
      <Icon size={sz} weight="fill" />
      {showLabel && <span className="leading-none">{label}</span>}
    </span>
  );
}

export function UserBadgeList({ badges, size = 'sm', showLabel = true, className = '' }) {
  const primary = pickPrimary(badges);
  if (!primary) return null;
  return (
    <UserBadge
      type={primary.badge_type}
      rank={primary.rank}
      size={size}
      showLabel={showLabel}
      className={className}
    />
  );
}
